import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { formatUserNameWithStatus } from "components/users/UserName";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useUsers } from "lib/hooks/api/useUsers";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { SavedViewType } from "lib/types/savedViews";
import { useNoticeListsPageState } from "../useNoticeListsPageState";
import ViewOptions from "./ViewOptions";

import css from "./ViewHeaderText.module.scss";

type Props = {
  view: SavedViewType;
  pinnedViews: SavedViewType[];
};

export function ViewHeaderText({ view, pinnedViews }: Props) {
  const [viewStatus, setViewStatus] = useState<{
    status: "saved" | "unsaved" | "standard";
    message: string;
  }>();
  const { hasUserModifiedView } = useNoticeListsPageState();
  const currentUser = useAssertCurrentUser();
  // includeInactive: true so an owner who has since been deactivated still resolves.
  const { data: users } = useUsers({ includeInactive: true });

  // reset the form on change
  useEffect(() => {
    if (!users) return;

    const ownerId = view.owner?.guid;
    let noticeViewOwner;

    if (ownerId && currentUser.guid !== ownerId) {
      noticeViewOwner = users.find((user) => user.guid === ownerId);
    }

    if (noticeViewOwner) {
      setViewStatus({
        status: "unsaved",
        message: `Owned by ${formatUserNameWithStatus(noticeViewOwner)}`,
      });
    } else if (hasUserModifiedView) {
      setViewStatus({ status: "unsaved", message: "Unsaved changes" });
    } else if (view.isStandardView) {
      setViewStatus({ status: "standard", message: "Template" });
    } else {
      setViewStatus({ status: "saved", message: "Saved" });
    }
  }, [view, currentUser.guid, users, hasUserModifiedView]);

  return (
    <Flex justifyContent="space-between">
      <Flex gap={8} alignItems="center">
        <Text h1>{view.name}</Text>
        {viewStatus ? (
          <ViewStatus description status={viewStatus.status}>
            {viewStatus.message}
          </ViewStatus>
        ) : (
          <UIcon icon="spinner" size={24} />
        )}
      </Flex>
      <ViewOptions selectedView={view} pinnedViews={pinnedViews} />
    </Flex>
  );
}

export function NoViewHeaderText({ pinnedViews }: { pinnedViews: SavedViewType[] }) {
  return (
    <Flex column flexGrow={1}>
      <Flex>
        <div className={`${css.currentViewTitle} ${css.currentViewTitleContainer}`}>
          Find notices
        </div>
        <ViewOptions selectedView={undefined} pinnedViews={pinnedViews} />
      </Flex>
      <div className={css.currentViewDescription}>Search all public sector procurement notices</div>
    </Flex>
  );
}

const ViewStatus = styled(Text)<{ status: "saved" | "unsaved" | "standard" }>(
  ({ theme, status }) => ({
    padding: "2px 8px",
    ...(status === "saved" && {
      background: theme.colors.sysPrimarySubtle,
      color: theme.colors.sysTextDefault,
    }),
    ...(status === "unsaved" && {
      background: theme.colors.sysWarningSubtle,
      color: "#7C4F07",
    }),
    ...(status === "standard" && {
      background: theme.colors.sysBackgroundAlternative,
      color: theme.colors.sysTextSecondary,
    }),
  }),
);
